import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useCallback, useEffect } from 'react';

import Toolbar from './Toolbar';

const EDITOR_EMPTY_STATES = ['<p></p>', '<p><br></p>', '<div></div>'];

const TipTapEditorImproved = ({
  field,
  form,
  showToast,
  content,
  setContent,
  isEditable = true,
  hasHeadings = true,
  isAIDescriptionGenerated = false,
  setIsAIDescriptionGenerated = () => {},
  ...props
}) => {
  // const convertLineBreaksToBr = (text) => {
  //   return text.replace(/\r\n|\r|\n/g, '<br>');
  // };

  const convertPlainTextToTipTapHTMLWithSpacing = (text) => {
    const normalized = text.replace(/\r\n|\r/g, '\n');

    const parts = normalized.split(/(\n+)/);

    let html = '';
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (part.match(/^\n+$/)) {
        const extraParagraphs = part.length - 1;
        html += '<p></p>'.repeat(extraParagraphs);
      } else if (part.trim()) {
        html += `<p>${part.trim()}</p>`;
      }
    }
    return html;
  };

  const contentFromApi = convertPlainTextToTipTapHTMLWithSpacing(
    field?.value || content || '',
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
    ],
    content: contentFromApi,

    onBlur: ({ editor }) => {
      const html = editor.getHTML().replace(/\s/g, '');
      const isEmpty = EDITOR_EMPTY_STATES.includes(html);
      // Integrate with Formik
      form &&
        field &&
        form.setFieldValue(field.name, isEmpty ? '' : editor.getHTML());
      // Integrate with local state
      setContent && setContent(isEmpty ? '' : editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditable);
    }
  }, [editor, isEditable]);

  useEffect(() => {
    if (isAIDescriptionGenerated && editor && content) {
      const aiContent = convertPlainTextToTipTapHTMLWithSpacing(content);
      editor.commands.setContent(aiContent);
      setIsAIDescriptionGenerated(false);
    }
  }, [isAIDescriptionGenerated, editor, content, setIsAIDescriptionGenerated]);

  const setLink = useCallback(() => {
    if (!editor) return;

    const { state, commands } = editor;
    const { selection } = state;
    const previousUrl = editor.getAttributes('link').href || '';

    if (selection.empty) {
      alert('Please select some text to apply the link.');
      return;
    }

    let url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url.trim() === '') {
      // Remove the link
      commands.unsetLink(); // Use unsetLink instead of removeMark
      showToast && showToast('positive', 'Link removed successfully');
      return;
    }

    if (!/^(https?|ftp):\/\//i.test(url)) {
      if (!previousUrl) {
        url = `https://${url}`;
      }
    }

    // Add or update the link
    commands.setLink({ href: url });
    editor.commands.focus();

    showToast && showToast('positive', 'Link added successfully');
  }, [editor, showToast]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  return (
    <div
      className={`rich-text-editor ${
        isEditable ? 'border border-gray-100 rounded-md p-2' : ''
      } `}
    >
      {editor && isEditable && (
        <Toolbar editor={editor} setLink={setLink} hasHeadings={hasHeadings} />
      )}
      <EditorContent
        editor={editor}
        className='prose prose-sm max-w-full'
        {...props}
      />
    </div>
  );
};

export default TipTapEditorImproved;
